/**
 * `selectFreshRolls` — the pure freshness decision behind {@link ./DiceFeedSubscriber}, kept out
 * of React (like `throwProjection.ts`/`diceFade.ts`) so the history-vs-live split is headlessly
 * testable.
 *
 * **The bug this avoids:** seeding an "already seen" baseline on the subscriber's own first
 * observation. `useGameSession`'s `feed` starts `[]` and only fills in asynchronously when
 * `game_welcome` arrives, so that first effect pass records an EMPTY baseline; the welcome
 * backfill then lands and every historical entry reads as "unseen," throwing the most recent roll
 * on every page load/reload.
 *
 * **The fix:** freshness is judged against a caller-supplied `highWater` timestamp seeded from
 * the welcome backfill itself (`GameSession.feedBaselineTs`), not from anything observed
 * client-side at mount. A roll is fresh iff its server-stamped `ts` is strictly greater than
 * `highWater`. Because `ts` is non-decreasing in feed order and server-stamped (no client clock
 * skew to reason about), `ts > highWater` is both the freshness test AND, combined with
 * raising `highWater` to the feed's max `ts` after every call, the de-dup mechanism: a later
 * call over a feed that still contains an already-thrown roll won't re-throw it, since that
 * roll's `ts` is no longer greater than the (raised) high-water mark.
 */
import type { FeedEntry, RollFeedEntry } from "@lancer-vtt/shared";

/** The result of one freshness pass: the roll entries to throw + the new high-water mark. */
export type FreshRollsResult = {
  /** `kind: "roll"` entries with `ts > highWater`, in feed order. */
  rolls: RollFeedEntry[];
  /** `max(highWater, ...feed.map(e => e.ts))` — monotonically non-decreasing across calls. */
  nextHighWater: number;
};

/**
 * Select the roll entries in `feed` newer than `highWater` (strictly — a `ts` equal to the
 * high-water mark is already-seen, not fresh), and compute the next high-water mark. Pure and
 * total: an empty feed returns no rolls and leaves `highWater` unchanged.
 */
export function selectFreshRolls(feed: readonly FeedEntry[], highWater: number): FreshRollsResult {
  const rolls: RollFeedEntry[] = [];
  let nextHighWater = highWater;
  for (const entry of feed) {
    if (entry.kind === "roll" && entry.ts > highWater) rolls.push(entry);
    if (entry.ts > nextHighWater) nextHighWater = entry.ts;
  }
  return { rolls, nextHighWater };
}
